import styles from "./SearchPage.module.css";
import SearchResult from "./SearchResult/SearchResult";
import Pagination from "../../components/Pagination/Pagination";
import { Outlet, useLocation, useSearchParams, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { alternativeToSearch, SectorSearch, textSearch, useSearch, searchByGroups, searchByChemical, searchByCas } from "../../Api/Services/Search";
import { searchPageData } from "../../Models/SearchModel";
import { SearchContext } from "../../Store/Search.store";
import LoadingIcon from "../../components/LoadingIcon/LoadingIcon";
import SearchFilters from "./SearchFilters/SearchFilters";

interface StateProps {
  alternativeTo?: number;
  sectorCategory?: number;
  useCategory?: number;
  supplier?: string;
}

export default function SearchPage() {
  const [searchData, setSearchData] = useState<searchPageData>({
    currentPage: 1,
    data: {
      phrase: "",
      filters: {
        sectorCategories: [],
        useCategories: [],
        alternativeLevels: [],
        alternativeToCategories: [],
        certificationsCategories: [],
        suppliers: [],
      },
      items: [],
    },
    initialSearchItems: [],
    initialFilters: {
      sectorCategories: [],
      useCategories: [],
      alternativeLevels: [],
      alternativeToCategories: [],
      certificationsCategories: [],
      suppliers: [],
    },
    openFilter: 0,
  });
  // State contains id number of clicked category from Startpage
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [previousSlice, setPreviousSlice] = useState(0);
  const params = useParams();

  const search = searchParams.get("q");
  const groupsParam = searchParams.get("groups");
  const cas = params.cas; // Get CAS parameter from URL
  const location = useLocation();
  const { state } = location as { state: StateProps };

  useEffect(() => {
    setLoading(true);
    
    // Handle chemical search for /alternatives/chemical/:cas route
    if (cas) {
      searchByCas(cas).then((requestData) => {
        setSearchData((prevState) => ({
          ...prevState,
          data: requestData,
          initialSearchItems: requestData.items,
          initialFilters: requestData.filters,
        }));
        setLoading(false);
      }).catch((error) => {
        console.error("Error in chemical search:", error);
        setLoading(false);
      });
      return;
    }
    
    // Handle sinlist search for /alternatives/sinlist route
    if (location.pathname === "/alternatives/sinlist") {
      const sessionId = searchParams.get("s") || undefined;
      searchByChemical(sessionId).then((requestData) => {
        setSearchData((prevState) => ({
          ...prevState,
          data: requestData,
          initialSearchItems: requestData.items,
          initialFilters: requestData.filters,
        }));
        setLoading(false);
      }).catch((error) => {
        console.error("Error in sinlist search:", error);
        setLoading(false);
      });
      return;
    }
    
    // Handle groups search for /alternatives route
    if (location.pathname === "/alternatives" && groupsParam) {
      const groupIds = groupsParam.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (groupIds.length > 0) {
        searchByGroups(groupIds).then((requestData) => {
          setSearchData((prevState) => ({
            ...prevState,
            data: requestData,
            initialSearchItems: requestData.items,
            initialFilters: requestData.filters,
          }));
          setLoading(false);
        }).catch((error) => {
          console.error("Error in groups search:", error);
          setLoading(false);
        });
        return;
      }
    }
    
    // Handle existing search logic
    if (state == null) {
      textSearch(search ?? "").then((requestData) => {
        setSearchData((prevState) => ({
          ...prevState,
          data: requestData,
          initialSearchItems: requestData.items,
          initialFilters: requestData.filters,
        }));
        setLoading(false);
      }).catch((error) => {
        console.error("Error in text search:", error);
        setLoading(false);
      });
    } else if (state.alternativeTo != null) {
      alternativeToSearch(state.alternativeTo).then((requestData) => {
        // When coming from start page filters, ensure no filters are active initially
        const clearedFilters = {
          ...requestData.filters,
          alternativeToCategories: requestData.filters.alternativeToCategories.map(f => ({ ...f, isSelected: false })),
          sectorCategories: requestData.filters.sectorCategories.map(f => ({ ...f, isSelected: false })),
          useCategories: requestData.filters.useCategories.map(f => ({ ...f, isSelected: false })),
          alternativeLevels: requestData.filters.alternativeLevels.map(f => ({ ...f, isSelected: false })),
          suppliers: requestData.filters.suppliers.map(f => ({ ...f, isSelected: false })),
          certificationsCategories: requestData.filters.certificationsCategories.map(f => ({ ...f, isSelected: false }))
        };
        
        setSearchData((prevState) => ({
          ...prevState,
          data: {
            ...requestData,
            filters: clearedFilters
          },
          initialSearchItems: requestData.items,
          initialFilters: clearedFilters,
        }));
        setLoading(false);
      }).catch((error) => {
        console.error("Error in alternative search:", error);
        setLoading(false);
      });
    } else if (state.sectorCategory != null) {
      SectorSearch(state.sectorCategory).then((requestData) => {
        // When coming from start page filters, ensure no filters are active initially
        const clearedFilters = {
          ...requestData.filters,
          alternativeToCategories: requestData.filters.alternativeToCategories.map(f => ({ ...f, isSelected: false })),
          sectorCategories: requestData.filters.sectorCategories.map(f => ({ ...f, isSelected: false })),
          useCategories: requestData.filters.useCategories.map(f => ({ ...f, isSelected: false })),
          alternativeLevels: requestData.filters.alternativeLevels.map(f => ({ ...f, isSelected: false })),
          suppliers: requestData.filters.suppliers.map(f => ({ ...f, isSelected: false })),
          certificationsCategories: requestData.filters.certificationsCategories.map(f => ({ ...f, isSelected: false }))
        };
        
        setSearchData((prevState) => ({
          ...prevState,
          data: {
            ...requestData,
            filters: clearedFilters
          },
          initialSearchItems: requestData.items,
          initialFilters: clearedFilters,
        }));
        setLoading(false);
      }).catch((error) => {
        console.error("Error in sector search:", error);
        setLoading(false);
      });
    } else if (state.useCategory != null) {
      useSearch(state.useCategory).then((requestData) => {
        // When coming from start page filters, ensure no filters are active initially
        const clearedFilters = {
          ...requestData.filters,
          alternativeToCategories: requestData.filters.alternativeToCategories.map(f => ({ ...f, isSelected: false })),
          sectorCategories: requestData.filters.sectorCategories.map(f => ({ ...f, isSelected: false })),
          useCategories: requestData.filters.useCategories.map(f => ({ ...f, isSelected: false })),
          alternativeLevels: requestData.filters.alternativeLevels.map(f => ({ ...f, isSelected: false })),
          suppliers: requestData.filters.suppliers.map(f => ({ ...f, isSelected: false })),
          certificationsCategories: requestData.filters.certificationsCategories.map(f => ({ ...f, isSelected: false }))
        };
        
        setSearchData((prevState) => ({
          ...prevState,
          data: {
            ...requestData,
            filters: clearedFilters
          },
          initialSearchItems: requestData.items,
          initialFilters: clearedFilters,
        }));
        setLoading(false);
      }).catch((error) => {
        console.error("Error in use search:", error);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [state, search, groupsParam, location.pathname, cas, searchParams]);
  
  // Update previous slice when current page changes
  useEffect(() => {
    // Calculate the start index for pagination (0-based)
    setPreviousSlice((searchData.currentPage - 1) * 16);
  }, [searchData.currentPage]);

  const searchValueData = { searchData, setSearchData };
  return (
    <SearchContext.Provider value={searchValueData}>
      <div className={`${styles.search_container} container`}>
        <Outlet />
        <h1>
          Safer alternatives
          {searchData?.data.phrase && (
            <>
              {" "}for{" "}
              <span>
                “{searchData?.data.phrase ?? searchData?.data.category}”
              </span>
            </>
          )}
        </h1>
        <span className={styles.total_hits}>
          {searchData && searchData?.data.items.length > 0
            ? searchData.data.items.length + " available alternatives found"
            : "No alternatives found"}
        </span>
        <div className={styles.filters}>
          <SearchFilters />
        </div>
        <div className={styles.search_result_container}>
          {loading && <LoadingIcon />}
          {searchData.data.items
            .slice(previousSlice, previousSlice + 16)
            .map((item, index) => (
              <SearchResult
                key={index}
                item={item}
                quality={item.alternativeLevel}
              />
            ))}
        </div>
        <Pagination />
      </div>
    </SearchContext.Provider>
  );
}
